import React, { useEffect, useState, useMemo } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import { Card } from '~/components';
import { useAuth, useRouter } from '~/hooks';
import { SectionNav } from '~/components/Section';
import Layout from '../templates/DefaultLayout';
import { AiFillHome, AiFillDollarCircle  } from 'react-icons/ai';
import { MdPrint, MdContentCopy } from 'react-icons/md';
import { TiTickOutline, CgNotes, IoMdMap, BsBellFill, AiFillProfile, MdDirectionsWalk, GiCheckMark, RiEditFill } from 'react-icons/all';
import { PropertyInfo, TaxesAndSpecialAssesments, RestrictiveCovenants, RecordedPlat, Liens, Easements, TitleInsuranceApplication, AdditionalInformation, Review, Submit } from './sections';
import { titleOpinionApi } from '~/api';
import { titleOpinionContext, withTitleOpinion , useSubmitReviewForm} from './hooks';
import { DateTime } from 'luxon';
import NextPrevious from '~/components/Section/NextPrevious';
import { generateDocument } from './generatePTODoc';
import sectionTypes from './sections/sectionTypes';
import SubmitRevision from './submitRevision';
import { document } from '~/api';
import { addAttachment, duplicateTitleOpinion as duplicateTitleOpinionApi } from '~/api/titleOpinion';
import { generatePXTFile } from './generatePXTFile';
import titleOpinionStatus from '~/util/titleOpinionStatus';
import dataFieldsToDuplicate from '~/util/dataFieldsToDuplicate';
import FileNumberPopup from './fileNumberPopup';

const INITIAL_PATH_NAMES = ["/preliminaryopinion/0", "/preliminaryopinion/0/propertyInfo"];

const PreliminaryTitleOpinion = () => {
    let { params, match: { url }, query } = useRouter('/preliminaryopinion/:id');
    const { pathname, replace, history } = useRouter();
    const { isTryon,user } = useAuth();
    
    const isRevision = query.get('action') === 'revision';

    const [titleOpinion, setTitleOpinion] = useState(null);
    const [submitRevision, setSubmitRevision] = useState(isRevision);
    const [successMessage, setSuccessMessage] = useState(false);
    const [duplicateSuccessMessage, setDuplicateSuccessMessage] = useState(false);    

    const existingOpinion = useMemo(() => titleOpinion && titleOpinion.id, [titleOpinion]);
    const orderNumber = titleOpinion ? titleOpinion.orderNumber : null;
    const allowRevision = (titleOpinion && titleOpinion.statusId == 2) ? true : false;
    const readOnly = (isTryon || (titleOpinion && titleOpinion.statusId == 2)) ? true : false;
    //const errorMessage = titleOpinion ? titleOpinion.errorMessage : null;
    const submittedOpinion = (titleOpinion && titleOpinionStatus.PTO.SUBMITTED == titleOpinion.statusId) ? true : false;
    
    const [isOpen, setOpen] = useState(false);

    useEffect(() => {
        const getOpinion = async () => {
            if (params.id && params.id != 0) {
                const opinion = await titleOpinionApi.getTitleOpinion(params.id);
                setTitleOpinion(opinion);
            }
        };
        getOpinion();
    }, [params.id]);

    const printPTODOC = async() => {
        generateDoc(true);
    };

    const generateDoc = async(isPrintOnly = false) => {
        let canGeneratePXTFile = user?.preferences?.sendPPTXFile;
        canGeneratePXTFile = canGeneratePXTFile == undefined || canGeneratePXTFile == '' ||  canGeneratePXTFile == "true" ? true : false;
        await titleOpinionApi.getPreliminaryTitleOpinionDoc(params.id)
            .then(async(res) => {
                return Promise.all([
                    generateDocument(res, params.id, isPrintOnly),
                    canGeneratePXTFile && generatePXTFile(res, params.id, isPrintOnly)
                ]);
            })
            .catch((err) => {
                alert(' Issue generating PTO documents. Please share error with support. Error: ' + err.message + ' stack:' + err.stack);
            })
            .then(async() => {
                //generate pto email with documents/attachments
                return !isPrintOnly && await titleOpinionApi.sendNewPreliminaryTitleOpinionEmail(params.id);
            }).catch((err) => alert(' Issue sending email.  Please share error with support. Error: ' + err));
    };
        
    const submitPTORevision = async (values) => {
        const details = {
            revisions: values.revisions,
            files: values.files ? values.files : []
        };
        setSubmitRevision(false);
        details.files.map(file => {
            const desc = "PTO Revision Document - Title Opinion Id - " + params.id;
            const attachment = { ...file.uploadInfo, typeId: document.DocumentType.PTO_REVISION, description: desc };
            document.upload(file);
            addAttachment(params.id ,attachment).then((res)=>{
                return res;
            }).catch((err) => {
                console.error(err);
            });
        });

        await titleOpinionApi.submitRevision(params.id, details);
        setSuccessMessage(true);
        setTimeout(() => {
            setSuccessMessage(false);
        }, 2500);                
    };

    const submitPTORequest = async() => {
        useSubmitReviewForm(params.id, sectionTypes.REVIEW_PTO).then(async value => 
        {
            if((Object.keys(value.errorSections).length !== 0) 
            && (Object.values(value.errorSections).filter(v=> v === true).length === 0) 
            && value.attorney 
            && value.attorney != null) {
                generateDoc();
                const submitResult = await titleOpinionApi.submitPTORequest(params.id);
                if (submitResult?.errored) {
                    alert("An error occurred. The PTO could not be submitted.");
                }
                else {
                    replace(pathname.replace(`/review`, `/submit`));
                }
                
            } else {
                alert(' Please complete the preliminary title opinion request to submit.');
            }
        });
        
    };

    const duplicateTitleOpinion =async()=> {
        const payload = {
            statusId: titleOpinionStatus.PTO.DRAFTED,
            titleOpinionId_original: params.id,
            dataToDuplicate: dataFieldsToDuplicate.PTO_DATA_FIELDS_DUPLICATE.dataToDuplicate
        };

        const result = await duplicateTitleOpinionApi(payload);
        if(+result?.id > 0) {
            setDuplicateSuccessMessage(true);
            setTimeout(() => {
                setDuplicateSuccessMessage(false);
            }, 15000);
            history.push("/preliminaryopinion/" + result.id);
        } else {
            alert("Unable to duplicate PTO");
        }
    };

    const overlayTitleOpinion =async()=> {
        if(+params.id <= 0 || params.id == undefined || params?.id == null) return;
        setOpen(true);
    };

    return (
        <Layout>
            <titleOpinionContext.Provider value={{ titleOpinion, existingOpinion, titleOpinionId: params.id, readOnly: readOnly, orderNumber: orderNumber}}>
                <div className="container h-full flex flex-col">
                    <div className="flex flex-row justify-between">
                        <h1 className='text-blue-60 text-3xl font-semibold mt-12'>Preliminary Title Opinion</h1>
                        {successMessage ?                        
                            <Card className="h-9 mt-2 px-4 py-2 flex flex-row rounded-md bg-lightGreen-300 text-white focus:outline-none drop-shadow-none text-sm"> 
                                <div className="flex flex-row" ><GiCheckMark size="1.25rem"/> &nbsp; Revision Request submitted successfully. Thank You.</div> 
                            </Card>
                            :
                            <> </>
                        }
                        {duplicateSuccessMessage ?                        
                            <Card className="h-9 mt-2 px-4 py-2 flex flex-row rounded-md bg-lightGreen-300 text-white focus:outline-none drop-shadow-none text-sm"> 
                                <div className="flex flex-row" ><GiCheckMark size="1.25rem"/> &nbsp; Title Opinion duplicated successfully. Thank You.</div> 
                            </Card>
                            :
                            <> </>
                        }
                        {allowRevision ?
                            <div className="flex flex-row justify-center">
                                <button 
                                    className="mt-12 mr-2 py-2 px-4 flex justify-between rounded-md bg-blue-50 text-white focus:outline-none"
                                    onClick={() => duplicateTitleOpinion()}>
                                    <MdContentCopy size="1em" className="mr-1 mt-1" />
                                    Duplicate Order
                                </button>
                                <button 
                                    className="mt-12 py-2 px-4 flex ml-4 rounded-md bg-blue-50 text-white focus:outline-none"
                                    onClick={() => setSubmitRevision(true)}>
                                    <RiEditFill size="1em" className="mr-1 mt-1" />
                                    Submit Revision
                                </button>
                                <button 
                                    className="mt-12 py-2 px-4 flex  ml-4 rounded-md bg-blue-50 text-white focus:outline-none"
                                    onClick={printPTODOC}>
                                    <MdPrint size="1em" className="mr-1 mt-1" />
                                    Print
                                </button>
                            </div>
                            :
                            <div className="flex flex-row justify-center">
                                {!submittedOpinion && !INITIAL_PATH_NAMES.includes(pathname) &&
                                <>
                                    <span
                                        className='transititext-primary text-primary transition duration-150 ease-in-out hover:text-primary-600 focus:text-primary-600 active:text-primary-700 dark:text-primary-400 dark:hover:text-primary-500 dark:focus:text-primary-500 dark:active:text-primary-600' 
                                        data-te-toggle="tooltip"
                                        title="This action will over-write all of the information contained in this file with the content of the file number you enter."
                                    >
                                        <button
                                            className="mt-12 mr-2 py-2 px-4 flex justify-between rounded-md bg-blue-50 text-white focus:outline-none"
                                            onClick={() => overlayTitleOpinion()}>
                                            <MdContentCopy size="1em" className="mr-1 mt-1" />
                                            Overlay Order
                                        </button>
                                    </span>
                                    <FileNumberPopup isOpen={isOpen} setOpen={setOpen} titleOpinionId={params.id}/>
                                </>
                                }
                                {
                                    !INITIAL_PATH_NAMES.includes(pathname) &&
                                    <>
                                        <button 
                                            className="mt-12 mr-2 py-2 px-4 flex justify-between rounded-md bg-blue-50 text-white focus:outline-none"
                                            onClick={() => duplicateTitleOpinion()}>
                                            <MdContentCopy size="1em" className="mr-1 mt-1" />
                                            Duplicate Order
                                        </button>
                                        <button 
                                            className="mt-12 py-2 px-4 flex justify-between rounded-md bg-blue-50 text-white focus:outline-none"
                                            onClick={printPTODOC}>
                                            <MdPrint size="1em" className="mr-1 mt-1" />
                                            Print
                                        </button>                                    
                                    </>
                                }
                            </div> 
                        }
                        
                    </div>
                    <div className='flex items-center text-xs'>
                        {existingOpinion && (
                            <React.Fragment>
                                <p>
                                    <span className='font-semibold'>Last Updated On </span>
                                    {DateTime.fromISO(titleOpinion.modified).toLocaleString(DateTime.DATETIME_FULL)}
                                </p>
                                <p className="ml-4 my-2"><span className='font-semibold'>Updated By </span>{`${titleOpinion.modifiedBy.firstName} ${titleOpinion.modifiedBy.lastName}`}</p>
                            </React.Fragment>
                        )}
                    </div>
                    {existingOpinion && orderNumber ?
                        <Card className="w-full h-8 bg-lightGreen-300 text-sm text-white p-2 rounded-md mb-0 drop-shadow-none"> 
                            <p> {`Order - ${titleOpinion.orderNumber} Submitted on ${DateTime.fromISO(titleOpinion.submitted).toLocaleString()} by ${titleOpinion.submittedBy.firstName} ${titleOpinion.modifiedBy.lastName}`}</p> 
                        </Card>
                        :
                        existingOpinion && allowRevision ? 
                            <Card className="w-full h-8 bg-lightGreen-300 text-sm text-white p-2 rounded-md mb-0 drop-shadow-none"> 
                                <p>{`Submitted on ${DateTime.fromISO(titleOpinion.submitted).toLocaleString()} by ${titleOpinion.submittedBy.firstName} ${titleOpinion.submittedBy.lastName}`}</p> 
                            </Card>
                            : 
                            <> </>
                        
                    }
                    
                    {submitRevision ?                        
                        <SubmitRevision  
                            isModalOpen={submitRevision}
                            onCancel={() => setSubmitRevision(false)}
                            onSubmit={(values) => submitPTORevision(values)}
                            headerTxt={'Submit Revision'}
                        />
                        :
                        <> </>
                    }
                    
                    <SectionNav className="mt-10">
                        <SectionNav.Link to={`${url}/propertyInfo`} icon={AiFillHome} text='Property Information' />
                        <SectionNav.Link to={`${url}/taxes`} icon={AiFillDollarCircle} text='Taxes &amp; Special Assessments' />
                        <SectionNav.Link to={`${url}/covenants`} icon={CgNotes} text='Restrictive Covenants &amp; Conditions' />
                        <SectionNav.Link to={`${url}/plat`} icon={IoMdMap} text='Recorded Plat' />
                        <SectionNav.Link to={`${url}/liens`} icon={BsBellFill} text='Liens, Mortgages, &amp; Deeds of Trust'/>
                        <SectionNav.Link to={`${url}/easements`} icon={MdDirectionsWalk} text='Easements &amp; Rights of Way'/>
                        <SectionNav.Link to={`${url}/titleInsurance`} icon={AiFillProfile} text='Title Insurance Application'/>
                        <SectionNav.Link to={`${url}/additionalInfo`} icon={AiFillHome} text='Additional Information'/>
                        <SectionNav.Link to={`${url}/review`} icon={TiTickOutline} text='Final Review'/>
                    </SectionNav>
                    <Card className="flex-1 my-8 rounded-md py-4 px-2">
                        <div className="flex flex-col h-full items-stretch">
                            <Switch>
                                <Redirect path={url} exact to={`${url.trimEnd('/')}/propertyInfo`} />
                                <Route path={`${url}/propertyInfo`} component={() => withTitleOpinion(PropertyInfo)} />
                                <Route path={`${url}/taxes`} component={() => withTitleOpinion(TaxesAndSpecialAssesments)} />
                                <Route path={`${url}/covenants`} component={() => withTitleOpinion(RestrictiveCovenants)} />
                                <Route path={`${url}/plat`} component={() => withTitleOpinion(RecordedPlat)}/>
                                <Route path={`${url}/liens`} component={() => withTitleOpinion(Liens)}/>
                                <Route path={`${url}/easements`} component={() => withTitleOpinion(Easements)}/>
                                <Route path={`${url}/titleInsurance`} component={() => withTitleOpinion(TitleInsuranceApplication)}/>
                                <Route path={`${url}/additionalInfo`} component={() => withTitleOpinion(AdditionalInformation)}/>
                                <Route path={`${url}/review`} component={() => withTitleOpinion(Review)}/>
                                <Route path={`${url}/submit`} component={() => withTitleOpinion(Submit)}/>
                            </Switch>
                            <NextPrevious sections={['propertyInfo', 'taxes', 'covenants', 'plat', 'liens', 'easements', 'titleInsurance', 'additionalInfo', 'review']} 
                                submit={submitPTORequest}
                                canSubmit={!readOnly}
                            />               
                        </div>
                    </Card>                    
                </div>
            </titleOpinionContext.Provider>
        </Layout>
    );
};

export default PreliminaryTitleOpinion;
